import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { CheckIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { API_URL } from "../utils/constants";
import { showToast } from "../utils/toast";
import { useLoginResult } from "../State/LoginResultContext";
import { FaCheck } from "react-icons/fa";
import { FaCircleCheck } from "react-icons/fa6";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function formatShortDate(dateLike) {
  try {
    const d = new Date(dateLike);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "-";
  }
}

function nameFromEmail(email) {
  if (!email || typeof email !== "string") return "—";
  const local = email.split("@")[0] || "";
  const cleaned = local
    .replace(/[._-]+/g, " ")
    .replace(/\d+/g, "")
    .trim();
  if (!cleaned) return "—";
  return cleaned.replace(/\b\w/g, (c) => c.toUpperCase());
}

const TABLE_WRAP_CLASS =
  "rounded-[10px] border border-white bg-[#17002e]/35 overflow-hidden";

function TableSkeleton({ columns, rowCount = 5 }) {
  return (
    <div className={TABLE_WRAP_CLASS}>
      <div className="overflow-x-auto">
        <table className="table w-full border-collapse w-full atkinson-font table-fixed">
          <colgroup>
            {columns.map((c) => (
              <col
                key={c.key}
                style={c.width ? { width: c.width } : undefined}
              />
            ))}
          </colgroup>
          <thead>
            <tr className="bg-[#0E011D]/55 border-b border-white">
              {columns.map((c) => (
                <th
                  key={c.key}
                  className="text-white font-medium px-2 sm:px-6 py-3 text-left"
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-white">
            {Array.from({ length: rowCount }).map((_, idx) => (
              <tr key={`sk-${idx}`} className="bg-transparent">
                {columns.map((c) => (
                  <td key={`sk-${idx}-${c.key}`} className="px-2 sm:px-6 py-3">
                    <div className="h-4 w-[85%] rounded shimmer opacity-80" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div
      className={classNames(
        TABLE_WRAP_CLASS,
        "min-h-[140px] flex items-center justify-center p-8 sm:p-10",
      )}
    >
      <p className="text-white text-center atkinson-font text-base sm:text-[16px]">
        {message}
      </p>
    </div>
  );
}

function ResponsiveTable({
  columns,
  rows,
  mobileCard, // Unused now, but kept in signature to avoid breaking call sites immediately
  isLoading,
  emptyMessage,
}) {
  return (
    <>
      {isLoading ? <TableSkeleton columns={columns} /> : null}
      {!isLoading && rows.length === 0 ? (
        <EmptyState message={emptyMessage || "Nothing to show yet."} />
      ) : null}
      {!isLoading && rows.length > 0 ? (
        <div className={TABLE_WRAP_CLASS}>
          <div className="overflow-x-auto">
            <table className="table w-full border-collapse w-full atkinson-font table-fixed">
              <colgroup>
                {columns.map((c) => (
                  <col
                    key={c.key}
                    style={c.width ? { width: c.width } : undefined}
                  />
                ))}
              </colgroup>
              <thead>
                <tr className="bg-[#17002E] border-b border-white">
                  {columns.map((c) => (
                    <th
                      key={c.key}
                      className="text-white font-medium px-2 sm:px-6 py-3 text-left tracking-tight"
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white">
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="bg-[#17002E] hover:bg-[#27004E] transition-colors"
                  >
                    {columns.map((c) => (
                      <td
                        key={c.key}
                        className="px-2 sm:px-6 py-3 text-white tracking-tight break-words"
                      >
                        {typeof c.render === "function"
                          ? c.render(row)
                          : row[c.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </>
  );
}

/**
 * Team members / invites screen.
 *
 * - Provide `members` and `invites` from API later.
 * - Provide `onInvite(email)` to send invite; can return a Promise.
 */
export default function TeamMembers({
  members,
  invites,
  onInvite,
  title = "Team Members",
}) {
  const { loginResults } = useLoginResult();
  const demoMembers = useMemo(
    () => [
      {
        id: "m-1",
        name: "John Doe",
        email: "Johndoe@gmail.com",
        joinedAt: "2025-12-17",
      },
      {
        id: "m-2",
        name: "Jane Doe",
        email: "Janedoe@gmail.com",
        joinedAt: "2025-03-03",
      },
      {
        id: "m-3",
        name: "Stacy Deedoe",
        email: "Stacydeedoe@gmail.com",
        joinedAt: "2025-08-26",
      },
    ],
    [],
  );

  const demoInvites = useMemo(
    () => [
      {
        id: "i-1",
        name: "John Doe",
        email: "Johndoe@gmail.com",
        invitedAt: "2025-12-18",
      },
      {
        id: "i-2",
        name: "Jane Doe",
        email: "Janedoe@gmail.com",
        invitedAt: "2025-12-20",
      },
    ],
    [],
  );

  const memberRows = members ?? demoMembers;
  const inviteRows = invites ?? demoInvites;

  const [apiMembers, setApiMembers] = useState(null);
  const [apiInvites, setApiInvites] = useState(null);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [stage, setStage] = useState("form"); // "form" | "success"
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sentTo, setSentTo] = useState("");

  const authHeaders = useMemo(() => {
    const token = loginResults?.token;
    if (!token) return null;
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }, [loginResults?.token]);

  const fetchMembers = async () => {
    if (!authHeaders) return;
    setLoadingMembers(true);
    setLoadError("");
    try {
      const res = await fetch(`${API_URL}/users/members`, {
        method: "GET",
        headers: authHeaders,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Unable to load members.");

      const normalized = Array.isArray(data)
        ? data.map((u) => ({
            id: u.id || u._id || u.email,
            name: u.fullName || u.name || u.organization || "Member",
            email: u.email,
            joinedAt: u.createdDate || u.createdAt || u.joinedAt || null,
          }))
        : [];
      setApiMembers(normalized);
    } catch (e) {
      setLoadError(e?.message || "Unable to load members.");
    } finally {
      setLoadingMembers(false);
    }
  };

  const fetchInvites = async () => {
    if (!authHeaders) return;
    setLoadingInvites(true);
    setLoadError("");
    try {
      const res = await fetch(`${API_URL}/users/invites`, {
        method: "GET",
        headers: authHeaders,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Unable to load invites.");

      const normalized = Array.isArray(data)
        ? data.map((inv) => ({
            id: inv.id || inv._id || inv.tokenHash || inv.email,
            name: inv.name || nameFromEmail(inv.email),
            email: inv.email,
            invitedAt:
              inv.createdDate || inv.createdAt || inv.invitedAt || null,
          }))
        : [];
      setApiInvites(normalized);
    } catch (e) {
      setLoadError(e?.message || "Unable to load invites.");
    } finally {
      setLoadingInvites(false);
    }
  };

  useEffect(() => {
    // Only auto-load from API when parent isn't providing data
    if (!authHeaders) return;
    if (members == null) fetchMembers();
    if (invites == null) fetchInvites();
  }, [authHeaders, members, invites]);

  const close = () => {
    setOpen(false);
    window.setTimeout(() => {
      setStage("form");
      setBusy(false);
      setError("");
      setSentTo("");
      setEmail("");
    }, 150);
  };

  const submitInvite = async (e) => {
    e.preventDefault();

    if (!(loginResults?.isAdminUser && (loginResults?.isMultisitePlan || loginResults?.isSinglesitePlan))) {
      showToast("You are not authorized to perform this action!", {
        type: "error",
      });
      return;
    }

    setError("");
    const trimmed = email.trim();
    if (!trimmed) return;

    setBusy(true);
    try {
      if (typeof onInvite === "function") {
        await onInvite(trimmed);
      } else if (authHeaders) {
        const res = await fetch(`${API_URL}/users/inviteMember`, {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify({ email: trimmed }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || "Unable to send invite.");
        await fetchInvites();
        showToast("Invite sent successfully.", { type: "success" });
      } else {
        showToast("Upcoming feature!", { type: "info" });
        close();
        return;
      }
      setSentTo(trimmed);
      setStage("success");
    } catch (err) {
      setError(err?.message || "Something went wrong sending the invite.");
    } finally {
      setBusy(false);
    }
  };


  const primaryButtonClass =
    "bg-[#5701AE] text-white text-base rounded-lg py-[10px] px-8 font-semibold shadow-[0_0_0_1px_rgba(255,255,255,0.2)] hover:bg-[#7c4dff] transition-colors disabled:opacity-60 disabled:cursor-not-allowed";

  const resolvedMembers = apiMembers ?? memberRows;
  const resolvedInvites = apiInvites ?? inviteRows;

  return (
    <div className="text-white font-sans pb-10">
      <div className="max-w-6xl mx-auto px-6">
        <div className="pt-4 sm:pt-10 text-center">
          <h1 className="text-[32px] sm:text-[40px] font-bold atkinson-font tracking-tight">
            {title}
          </h1>
        </div>
        <button
          type="button"
          className="flex w-full mt-8 sm:w-[300px] justify-center rounded-md !bg-[#5701AE] p-2 text-base font-semibold text-white hover:!bg-[#7A2FD3] sm:hidden"
          style={{ border: "2px solid #6A6680" }}
          onClick={() => setOpen(true)}
        >
          Send Invite
        </button>
        {/* Current Members row (heading + CTA) */}
        <div className="mt-6 sm:mt-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-[16px] sm:text-[32px] font-bold atkinson-font sm:justify-self-end">
            Current Members
          </h2>
          <button
            type="button"
            className="w-full sm:w-[300px] justify-center rounded-md !bg-[#5701AE] p-2 text-base font-semibold text-white hover:!bg-[#7A2FD3] hidden sm:flex"
            style={{ border: "2px solid #6A6680" }}
            onClick={() => setOpen(true)}
          >
            Send Invite
          </button>
        </div>

        {loadError ? (
          <div className="mt-4 rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-100 atkinson-font">
            {loadError}
          </div>
        ) : null}

        <div className="mt-4">
          <ResponsiveTable
            columns={[
              { key: "name", label: "Name", width: "30%" },
              { key: "email", label: "Email", width: "50%" },
              {
                key: "joinedAt",
                label: "Joined",
                width: "20%",
                render: (r) => formatShortDate(r.joinedAt),
              },
            ]}
            rows={resolvedMembers}
            isLoading={loadingMembers}
            emptyMessage="No team members yet. Send an invite to get started."
            mobileCard={(r) => (
              <div className="atkinson-font">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-white">{r.name}</div>
                    <div className="text-white">{r.email}</div>
                  </div>
                  <div className="text-white">
                    {formatShortDate(r.joinedAt)}
                  </div>
                </div>
              </div>
            )}
          />
        </div>

        <div className="mt-10 sm:mt-12">
          <h2 className="text-base sm:text-[32px] font-bold atkinson-font">
            Pending Invites
          </h2>

          <div className="mt-4">
            <ResponsiveTable
              columns={[
                { key: "name", label: "Name", width: "30%" },
                { key: "email", label: "Email", width: "50%" },
                {
                  key: "invitedAt",
                  label: "Invited",
                  width: "20%",
                  render: (r) => formatShortDate(r.invitedAt),
                },
              ]}
              rows={resolvedInvites}
              isLoading={loadingInvites}
              emptyMessage="No pending invites. When you invite someone, they’ll show up here."
              mobileCard={(r) => (
                <div className="atkinson-font">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-white">{r.name}</div>
                      <div className="text-white">{r.email}</div>
                    </div>
                    <div className="text-white">
                      {formatShortDate(r.invitedAt)}
                    </div>
                  </div>
                </div>
              )}
            />
          </div>
        </div>
      </div>

      {/* Invite modal */}
      <Transition appear show={open} as={Fragment}>
        <Dialog as="div" className="relative z-[60]" onClose={close}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="min-h-full flex items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95 translate-y-1"
                enterTo="opacity-100 scale-100 translate-y-0"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100 translate-y-0"
                leaveTo="opacity-0 scale-95 translate-y-1"
              >
                <Dialog.Panel className="w-full max-w-sm rounded-[20px] border border-[#6A6680] bg-[#230046] shadow-2xl">
                  <div className="p-2">
                    <div className="flex items-start justify-end gap-3">
                      <button
                        type="button"
                        className="!bg-transparent hover:!bg-transparent p-1 text-white hover:text-white"
                        onClick={close}
                        aria-label="Close"
                      >
                        <XMarkIcon className="h-8 w-8 text-[#6A6680] hover:text-[#FFFFFF]" />
                      </button>
                    </div>

                    {stage === "form" ? (
                      <div className="py-4">
                        <Dialog.Title className="text-center text-[white] text-[22px] font-bold atkinson-font">
                          Invite a Team Member
                        </Dialog.Title>

                        <form
                          onSubmit={submitInvite}
                          className="my-4 space-y-4"
                        >
                          <div>
                            <label className="block text-base font-bold text-white text-left atkinson-font">
                              Email
                            </label>
                            <div className="mt-2">
                              <input
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                type="email"
                                autoComplete="email"
                                required
                                placeholder="johndoe@gmail.com"
                                className="block w-full rounded-md border border-[#FFFFFFCC] py-2 px-3 bg-[#595959] text-[#FFFFFF] text-sm placeholder:text-[#FFFFFFCC] placeholder:text-gray-400 focus:border-[#FFFFFF]"
                              />
                            </div>
                          </div>

                          {error ? (
                            <div className="rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-100 atkinson-font">
                              {error}
                            </div>
                          ) : null}

                          <button
                            type="submit"
                            disabled={busy}
                            className="flex !mt-6 w-full justify-center rounded-md !bg-[#5701AE] p-2 text-base font-semibold text-white hover:!bg-[#7A2FD3]"
                            style={{ border: "2px solid #6A6680" }}
                          >
                            {busy ? "Inviting..." : "Invite"}
                          </button>
                        </form>
                      </div>
                    ) : (
                      <div className="p-4 pt-0 text-center">
                        <div className="mx-auto flex h-12 w-12 p-3 items-center justify-center rounded-full border border-[#6A6680] bg-white">
                          <FaCheck className="h-10 w-10" />
                        </div>
                        <Dialog.Title className="mt-4 text-[white] text-[22px] font-bold atkinson-font tracking-tight">
                          Invite Sent!
                        </Dialog.Title>
                        <p className="mt-2 text-[#FFFFFF] atkinson-font">
                          An invitation has been sent to{" "}
                          <span className="font-bold text-white">
                            {sentTo || "[Team Member email]"}
                          </span>
                          .
                        </p>

                        <button
                          type="button"
                          className="flex !mt-6 w-full justify-center rounded-md !bg-[#5701AE] p-2 text-base font-semibold text-white hover:!bg-[#7A2FD3]"
                          style={{ border: "2px solid #6A6680" }}
                          onClick={close}
                        >
                          Done
                        </button>
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
